<template>
  <div class="bg-gray-50 border-t border-gray-200 px-4 py-4">
    <LoadingIndicator v-if="loading" />
    <p v-else-if="error" class="text-red-600">{{ error }}</p>
    <template v-else-if="detail">
      <div class="flex items-start gap-4 mb-3">
        <p class="sensitive flex-1 whitespace-pre-wrap text-gray-900" data-demo-blur>{{ detail.prompt }}</p>
        <div class="flex shrink-0 items-center gap-2">
          <span v-if="caseMade" class="text-gray-700">Added as a case</span>
          <span v-else-if="caseError" class="text-red-600">{{ caseError }}</span>
          <button v-if="!caseMade" type="button" :class="SECONDARY_BUTTON" :disabled="making" @click="makeCase">{{ making ? 'Adding…' : 'Add as a case' }}</button>
          <a :href="detail.session_path" target="_blank" rel="noopener" :class="SECONDARY_BUTTON">Open session</a>
        </div>
      </div>
      <p v-if="!detail.steps.length" class="text-gray-500">Nothing recorded for this turn.</p>
      <ol class="space-y-1">
        <li v-for="(s, i) in detail.steps" :key="i" class="flex gap-3">
          <span class="w-14 shrink-0 text-right tabular-nums text-gray-500">{{ offset(s.offset) }}</span>
          <span :class="['w-24 shrink-0 rounded px-2 text-[11px] font-medium uppercase tracking-wider self-start', KIND_STYLE[s.kind] || 'bg-gray-100 text-gray-700']">{{ kindLabel(s) }}</span>
          <div class="min-w-0 flex-1">
            <template v-if="s.kind === 'tool'">
              <div class="flex flex-wrap items-center gap-2">
                <span class="font-mono text-gray-900">{{ s.name }}</span>
                <span v-if="s.seconds != null" :class="s.seconds > 30 ? 'font-medium text-red-700' : 'text-gray-500'">{{ seconds(s.seconds) }}</span>
                <span v-if="s.result_chars != null" class="text-gray-500">{{ tokens(s.result_chars) }} chars back</span>
                <span v-if="s.images" class="text-gray-500">{{ s.images }} image{{ s.images === 1 ? '' : 's' }}</span>
                <span v-if="s.ok === false" class="rounded bg-red-tint px-2 text-gray-900">error</span>
                <span v-if="s.ok === null" class="rounded bg-red-tint px-2 text-gray-900">no result</span>
                <button type="button" :class="TEXT_BUTTON" @click="toggle(i)">{{ open[i] ? 'Hide' : 'Show' }}</button>
              </div>
              <div v-if="open[i]" class="mt-1 space-y-1">
                <pre class="sensitive whitespace-pre-wrap break-words rounded bg-white border border-gray-200 p-2 text-[12px]" data-demo-blur>{{ s.args }}</pre>
                <pre v-if="s.result" class="sensitive whitespace-pre-wrap break-words rounded bg-white border border-gray-200 p-2 text-[12px]" data-demo-blur>{{ s.result }}</pre>
              </div>
            </template>
            <p v-else-if="s.kind === 'reply'" class="sensitive whitespace-pre-wrap text-gray-900" data-demo-blur>{{ s.text }}</p>
            <p v-else class="text-gray-700"><span class="font-medium">{{ s.name }}</span> {{ s.text }}</p>
          </div>
        </li>
      </ol>
    </template>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { API, SECONDARY_BUTTON, TEXT_BUTTON, errorOf, seconds, tokens } from '../hermes';

const KIND_STYLE = {
  tool: 'bg-neutral-100 text-gray-900',
  reply: 'bg-secondary-200 text-gray-900',
  hat: 'bg-primary-50 text-gray-900',
  interrupt: 'bg-red-tint text-gray-900',
  stopped: 'bg-red-tint text-gray-900',
  error: 'bg-red-tint text-gray-900',
  approval_request: 'bg-primary-100 text-gray-900',
  ask_user: 'bg-primary-100 text-gray-900',
};
const KIND_LABEL = { interrupt: 'stop pressed', stopped: 'stopped', approval_request: 'approval', ask_user: 'question' };

export default {
  name: 'ConsoleTurnDetail',
  components: { LoadingIndicator },
  props: { runId: { type: Number, required: true } },
  data() {
    return { detail: null, loading: true, error: '', open: {}, making: false, caseMade: false, caseError: '', KIND_STYLE, SECONDARY_BUTTON, TEXT_BUTTON };
  },
  async mounted() {
    try {
      const { data } = await this.$axios.get(`${API}/console/turns/${this.runId}/`);
      this.detail = data;
    } catch (e) {
      this.error = errorOf(e, 'The turn could not be loaded.');
    } finally {
      this.loading = false;
    }
  },
  methods: {
    seconds,
    tokens,
    offset(n) {
      return n == null ? '' : `+${seconds(n)}`;
    },
    kindLabel(s) {
      return KIND_LABEL[s.kind] || s.kind;
    },
    async makeCase() {
      this.making = true;
      this.caseError = '';
      try {
        await this.$axios.post(`${API}/console/bench/cases/`, { run_id: this.runId });
        this.caseMade = true;
      } catch (e) {
        this.caseError = errorOf(e, 'Could not add the case.');
      } finally {
        this.making = false;
      }
    },
    toggle(i) {
      this.open = { ...this.open, [i]: !this.open[i] };
    },
  },
};
</script>
